
import { createContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthState } from "@/hooks/useAuthState";
import { useAuthOperations } from "@/hooks/useAuthOperations";
import { useAuthSession } from "@/hooks/useAuthSession";
import type { AuthContextType } from "./auth-types";

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  isLoading: true,
  isSuperuser: false,
  isAdmin: false,
  isAuthReady: false,
  isRecoverySession: false,
  signInWithEmailAndPassword: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  verifyOtp: async () => {},
  resendVerificationEmail: async () => {},
  resetPassword: async () => false,
  updatePassword: async () => false,
  impersonateUser: async () => {},
  endImpersonation: async () => {},
  setProfile: () => {},
  supabase: supabase,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const authState = useAuthState();
  const authOperations = useAuthOperations(authState);
  const { isAuthReady } = useAuthSession(authState, authOperations.restoreImpersonation);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        ...authOperations,
        isAuthReady,
        setProfile: authState.setProfile,
        supabase,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
